import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, useState } from "react";
import { type ApiErrorResponse } from "@/config/apiClient.types";
import { getFlattenPermissions } from "@/helpers/rolePermissions/getFlattenPermissions";
import {
  accountsAtom,
  currentAccountIdAtom,
  currentWorkspaceIdAtom,
  userAtom,
  workspacesAtom,
} from "@/store";

export const useSuggestedFixRedirect = (query: {
  isError: boolean;
  error: ApiErrorResponse | null | undefined;
}) => {
  const workspaces = useAtomValue(workspacesAtom);
  const accounts = useAtomValue(accountsAtom);
  const user = useAtomValue(userAtom);
  const setCurrentAccountId = useSetAtom(currentAccountIdAtom);
  const setCurrentWorkspaceId = useSetAtom(currentWorkspaceIdAtom);

  const [is404Error, setIs404Error] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const permissions = getFlattenPermissions(user);

  // Handle potential suggestedFix return by the API
  // SuggestedFix gives the information on which account/orga/workspace set the user to get the requested data
  useEffect(() => {
    if (!query.isError) {
      return;
    }

    const suggestedFixData =
      query.error?.response?.data?.cause?.suggestedFixData;

    if (suggestedFixData && retryCount === 0) {
      const accountId = suggestedFixData?.accountId;
      const workspaceId = suggestedFixData?.workspaceId;

      if (
        !workspaceId ||
        !accountId ||
        typeof workspaceId !== "string" ||
        typeof accountId !== "string"
      ) {
        setIs404Error(true);
      } else {
        if (workspaces.find((e) => e.id === workspaceId)) {
          setCurrentWorkspaceId(workspaceId);
        } else {
          if (!accounts.find((e) => e.id === accountId)) {
            const organizationPartnershipId =
              permissions[workspaceId]?.organizationPartnership;

            if (organizationPartnershipId) {
              const accountFromOrg = Object.entries(
                user?.permissions ?? {}
              ).find(
                ([, accountPerms]) =>
                  accountPerms?.organizations?.[organizationPartnershipId]
              )?.[0];

              if (
                accountFromOrg &&
                accounts.find((e) => e.id === accountFromOrg)
              ) {
                setCurrentAccountId(accountFromOrg);
                setCurrentWorkspaceId(workspaceId);
                setRetryCount((e) => e + 1);

                return;
              }
            }

            setIs404Error(true);
            return;
          }

          setCurrentAccountId(accountId);
          setCurrentWorkspaceId(workspaceId);
        }

        setRetryCount((e) => e + 1);
      }
    } else {
      setIs404Error(true);
    }
  }, [query.isError]);

  return { is404Error };
};
